import { useForm } from 'react-hook-form'
// @ts-nocheck
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { sendMfaCode, verifyMfa } from '../api/auth'
import { useEffect, useRef, useState } from 'react'
import { getDeviceFingerprint } from '@shared/libs/device'
import { getPendingMfaToken } from '@shared/libs/authStorage'
import { http } from '@shared/libs/http'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@shared/toast/ToastProvider'
import { FormField } from '@shared/ui/FormField'
import { Input } from '@shared/ui/Input'
import { Button } from '@shared/ui/Button'
import { CodeInput6 } from '@shared/ui/CodeInput6'

const schema = z.object({
  pending_mfa_token: z.string().min(1),
  code: z.string().length(6),
  remember_device: z.boolean().optional()
})

type FormValues = z.infer<typeof schema>

export function MfaPage() {
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState<number>(0)
  const qc = useQueryClient()
  const presetToken = getPendingMfaToken() ?? ''
  const { register, handleSubmit, formState, setValue } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { pending_mfa_token: presetToken } })
  const { push } = useToast()

  const onSubmit = async (v: FormValues) => {
    setErr(null)
    try {
      const body = { ...v }
      if (v.remember_device) {
        // Attach deterministic device fingerprint if remember is checked
        ;(body as any).device_fingerprint = getDeviceFingerprint()
      }
      await verifyMfa(body)
      await qc.invalidateQueries({ queryKey: ['session'] })
      push({ type: 'success', title: 'MFA verified' })
      location.assign('/')
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? 'MFA verification failed')
    }
  }

  useEffect(() => {
    // Prime CSRF cookie for POSTs
    http.get('/auth/session').catch(() => {})
  }, [])

  // Auto-send code once on mount if we have a pending token
  const sentOnce = useRef(false)
  useEffect(() => {
    if (sentOnce.current) return
    if (presetToken) {
      sentOnce.current = true
      onSend()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetToken])

  const onSend = async () => {
    try {
      await sendMfaCode(presetToken || undefined)
      setMsg('Code sent to your email')
      setCooldown(30)
      const t = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) {
            clearInterval(t)
            return 0
          }
          return c - 1
        })
      }, 1000)
    } catch (e: any) {
      const m = e?.response?.data?.detail ?? 'Could not send code'
      setErr(m)
      push({ type: 'error', title: 'Send failed', description: m })
    }
  }

  return (
    <div className="min-h-screen grid place-items-center">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-6">
        <h1 className="text-xl font-semibold mb-2">Multi-factor authentication</h1>
        <p className="text-sm mb-4 text-muted">Enter the 6-digit code sent to your email.</p>
        {msg && <div className="mb-2 text-sm text-success">{msg}</div>}
        {err && <div className="mb-2 text-sm text-danger">{err}</div>}
        <form className="grid gap-3" noValidate onSubmit={handleSubmit(onSubmit)}>
          {presetToken ? null : (
            <FormField label="Pending MFA token">
              <Input {...register('pending_mfa_token')} />
            </FormField>
          )}
          <FormField label="Code" message={formState.errors.code?.message}>
            <CodeInput6 {...register('code')} />
          </FormField>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" onChange={(e) => setValue('remember_device', e.target.checked)} />
            <span>Remember this device</span>
          </label>
          <div className="flex gap-2">
            <Button disabled={formState.isSubmitting}>Verify</Button>
            <Button type="button" variant="outline" onClick={onSend} disabled={cooldown>0}>
              {cooldown>0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
// @ts-nocheck
